import React, { FC, useState } from 'react';
import AnimateHeight from 'react-animate-height';
import { useTranslation } from 'react-i18next';
import { FaMinus, FaPlus, FaUndo } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import {
	Location,
	ShipmentDeliveryServiceId,
	ShipmentDirection,
	ShipmentStatus,
} from '../../../../@posturinn/client-react';
import { setActiveModal } from '../../../../actions/modalActions';
import {
	ATTACH_FILES_MODAL,
	BARCODE_MODAL,
	DONT_HAVE_FILES_MODAL,
	PAY_MODAL,
	QR_CODE_MODAL,
} from '../../../../constants/documentTypes/modals/modals';
import { LABEL, MOBIZ_API_URL } from '../../../../constants/paths/api';
import { ReactComponent as Bin } from '../../../../icons/bin-simple.svg';
import { ReactComponent as IconAttention } from '../../../../icons/status_attention.svg';
import { ReactComponent as IconDone } from '../../../../icons/status_done.svg';
import { ReactComponent as IconInfo } from '../../../../icons/status_info.svg';
import { ReactComponent as IconEye } from '../../../../icons/eye.svg';
import { ReactComponent as IconEyeClosed } from '../../../../icons/eye-closed.svg';

import IUserProfileReducer from '../../../../interfaces/reducers/userProfileReducer';
import { IStore } from '../../../../reducers';
import Button from '../../../base/button';
import { Column } from '../../../base/grid';
import { A, P } from '../../../base/typography';
import DeleteShipmentModal from '../../../modals/deleteShipmentModal/deleteShipmentModal';
import HideShipmentModal from '../../modals/hideShipmentModal/hideShipmentModal';
import ShipmentDetails from '../shipmentDetails';
import * as StyledShipmentCard from './shipmentCard.styles';
import { fetchShipments, setShipmentVisibility } from '../../../../actions/shipmentsActions';
import media from '../../../../constants/media';

// changed shipment to any because it does not have the externalShipmentId property in the interface
// we are also getting rid of the package soon, the type will be declared later
interface Props {
	shipment: any;
	locations: Location[];
	delivered: boolean;
	page?: number;
	shipmentIndex?: number;
	open?: boolean;
	includeHidden?: boolean;
	showActions?: boolean;
}

const ShipmentCard: FC<Props> = ({ shipment, delivered, locations, page, shipmentIndex, open, includeHidden=false, showActions=true}: Props) => {
	const dispatch = useDispatch<any>();
	const { t } = useTranslation('myShipments');
	const location = locations.find(loc => loc.id === shipment.destinationLocationId);
	const [isOpen, setIsOpen] = useState<boolean>(open);
	const [deleteShipmentModal, setDeleteShipmentModal] = useState<boolean>(false);
	const [hideShipmentModal, setHideShipmentModal] = useState(false);
	
	const [eyeHovering, setEyeHovering] = useState(false);
	
	const userProfileReducer: IUserProfileReducer = useSelector<IStore, IUserProfileReducer>(
		store => store.userProfileReducer
	);
	const { userProfile } = userProfileReducer;
	
	
	const handleShipmentVisibilityClick = async () => {
		if (!shipment.hidden) setHideShipmentModal(true);
		else {
			await dispatch(setShipmentVisibility(shipment.id, !shipment.hidden));
			await dispatch(fetchShipments(page || 1, true, shipment.direction, includeHidden));
			await dispatch(fetchShipments(page || 1, false, shipment.direction, includeHidden));
		}
	};
	
	return (
		<StyledShipmentCard.ShipmentCard key={shipment.id} delivered={delivered}>
			<StyledShipmentCard.CardHeadingWrapper onClick={() => setIsOpen(!isOpen)}>
				<StyledShipmentCard.CardHeading>
					{shipment.direction === ShipmentDirection.Inbound ? shipment.sender.name : shipment.recipient.name}
				</StyledShipmentCard.CardHeading>
				<StyledShipmentCard.CardMore delivered={delivered}>
					{isOpen ? <FaMinus /> : <FaPlus />}
				</StyledShipmentCard.CardMore>
			</StyledShipmentCard.CardHeadingWrapper>
			<StyledShipmentCard.ShipmentInfo>
				<Column tablet={3 / 10}>
					<Grid>
						<SubGrid>
							<P black>{t('trackingNumber')}</P>
							{shipment.labelId ? shipment.labelId : shipment.id}
						</SubGrid>
						<SubGrid>
							{showActions && (
								<>
								<IconWrapper
									onClick={handleShipmentVisibilityClick}
									onMouseEnter={() => setEyeHovering(true)}
									onMouseLeave={() => setEyeHovering(false)}
									title={shipment.hidden ? t('common:show') : t('common:hide')}>
									{shipment.hidden ? (
										eyeHovering ? (
											<IconEye />
										) : (
											<IconEyeClosed />
										)
									) : eyeHovering ? (
										<IconEyeClosed />
									) : (
										<IconEye />
									)}
								</IconWrapper>
							
								{shipment.direction !== 'Inbound' && shipment.canBeDeleted && page && (
									<IconWrapper title={t('delete')} onClick={() => setDeleteShipmentModal(true)}>
										<StyledBin />
									</IconWrapper>
								)}
								</>
							)}
						</SubGrid>
					</Grid>
				</Column>
				{location ? (
					<Column tablet={4 / 10}>
						<P black>{t('page-overview.deliveryLocation')}</P>
						{location.name}
						{/* {false && (
							<A
								onClick={() =>
									dispatch(
										setActiveModal(
											SHOW_LOCATION_MODAL,
											null,
											null,
											null,
											// shipment.deliveryLocationLatitude,
											// shipment.deliveryLocationLongitude // TODO: FIX
											0,
											0
										)
									)
								}>
								{t('viewOnMap')}
							</A>
						)} */}
					</Column>
				) : shipment.deliveryServiceId === ShipmentDeliveryServiceId.HomeDelivery ? (
					<Column tablet={4 / 10}>
						<P black>{t('page-overview.deliveryLocation')}</P>
						{t('homeDelivery')}
					</Column>
				) : (
					<Column tablet={4 / 10}>
						<P black>{t('page-overview.deliveryLocation')}</P>
						{t('undecided')}
					</Column>
				)}
				<StyledShipmentCard.ShipmentStatus tablet={3 / 10}>
					{delivered ? (
						<IconDone />
					) : shipment.status === ShipmentStatus.ReturnedToSender ? (
						<FaUndo />
					) : shipment.isPayable ||
					  shipment.isMissingDocuments ||
					  shipment.status === ShipmentStatus.AvailableForPickup ? (
						<IconAttention />
					) : (
						<IconInfo />
					)}
					<div>
						<P black>{t('shipmentStatus')}</P>
						{t(`status.${shipment.status}`)}
					</div>
				</StyledShipmentCard.ShipmentStatus>
			</StyledShipmentCard.ShipmentInfo>
			<AnimateHeight duration={400} height={isOpen ? 'auto' : 0}>
				{isOpen ? (
					<ShipmentDetails
						shipment={shipment}
						delivered={delivered}
						page={page}
						dropOff={shipment.status === ShipmentStatus.PreTransit}
					/>
					)
					: <></>}
			</AnimateHeight>
			<StyledShipmentCard.MobileShowMore onClick={() => setIsOpen(!isOpen)}>
				<div>{isOpen ? <FaMinus /> : <FaPlus />}</div>
			</StyledShipmentCard.MobileShowMore>
			{shipment.isPayable && shipment.status !== ShipmentStatus.AvailableForPickupAtPostOffice && (
				<StyledShipmentCard.UnderneathBanner>
					<P>{`${t('amount')}: ${shipment.unpaidAmount + ' kr.'}`}</P>
					<Button
						primary
						onClick={() =>
							dispatch(
								setActiveModal(
									PAY_MODAL,
									t('page-overview.payFor') + shipment.id,
									null,
									null,
									null,
									null,
									shipment.id,
									page,
									shipmentIndex
								)
							)}>
						{t('pay')}
					</Button>
				</StyledShipmentCard.UnderneathBanner>
			)}
			{shipment.status === ShipmentStatus.AvailableForPickupAtPostBox && !shipment.isPayable && (
				<StyledShipmentCard.UnderneathBanner>
					<P>{t('availableForPickup')}</P>
					<Button
						onClick={() =>
							dispatch(
								setActiveModal(
									QR_CODE_MODAL,
									t('page-overview.openLocker'),
									null,
									null,
									null,
									null,
									shipment.id
								)
							)}>
						{t('page-overview.openLocker')}
					</Button>
				</StyledShipmentCard.UnderneathBanner>
			)}
			{shipment.status === ShipmentStatus.AvailableForPickupAtPostOffice && (
				<StyledShipmentCard.UnderneathBanner>
					<P>{t('availableForPickup')}</P>
					<div>
						<Button
							onClick={() =>
								dispatch(
									setActiveModal(
										BARCODE_MODAL,
										t('page-overview.barcode'),
										null,
										null,
										null,
										null,
										shipment.id,
										null,
										null,
										shipment.canBeDroppedOffAtPostboxOrParcelPoint
									)
								)}>
							{t('page-overview.barcode')}
						</Button>
						{shipment.isPayable && (
							<Button
								primary
								onClick={() =>
									dispatch(
										setActiveModal(
											PAY_MODAL,
											t('page-overview.payFor') + shipment.id,
											null,
											null,
											null,
											null,
											shipment.id,
											page,
											shipmentIndex
										)
									)}>
								{t('pay')}
							</Button>
						)}
					</div>
				</StyledShipmentCard.UnderneathBanner>
			)}
			{shipment.isMissingDocuments && (
				<StyledShipmentCard.UnderneathBanner>
					<P>{t('page-overview.missingCustomsInformation')}</P>
					<StyledShipmentCard.CtaLinks>
						<A
							onClick={() =>
								dispatch(
									setActiveModal(
										DONT_HAVE_FILES_MODAL,
										t('page-overview.customsInfoFor') + shipment.id,
										null,
										null,
										null,
										null,
										shipment.id,
										page
									)
								)}>
							{t('page-overview.missingDocuments')}
						</A>
						<Button
							primary
							onClick={() =>
								dispatch(
									setActiveModal(
										ATTACH_FILES_MODAL,
										t('page-overview.customsInfoFor') + shipment.id,
										null,
										null,
										null,
										null,
										shipment.id,
										page
									)
								)}>
							{t('page-overview.addDocuments')}
						</Button>
					</StyledShipmentCard.CtaLinks>
				</StyledShipmentCard.UnderneathBanner>
			)}
			{shipment.status === ShipmentStatus.PreTransit && shipment.direction === ShipmentDirection.Outbound && (
				<StyledShipmentCard.UnderneathBanner>
					<P>{t('availableForDropOff')}</P>
					<StyledShipmentCard.CtaLinks>
						<Button
							onClick={() =>
								dispatch(
									setActiveModal(
										BARCODE_MODAL,
										t('page-overview.barcode'),
										null,
										null,
										null,
										null,
										shipment.id,
										null,
										null,
										shipment.canBeDroppedOffAtPostboxOrParcelPoint
									)
								)}>
							{t('page-overview.barcode')}
						</Button>
						<A
							to={
								MOBIZ_API_URL +
								LABEL.replace('{senderNin}', userProfile.identificationNumber)
									.replace('{shipmentId}', shipment.id)
									.replace('{registrationKey}', shipment.registrationKey)
							}
							target="_blank">
							<Button>{t('page-overview.dropoffFile')}</Button>
						</A>
					</StyledShipmentCard.CtaLinks>
				</StyledShipmentCard.UnderneathBanner>
			)}
			{deleteShipmentModal && (
				<DeleteShipmentModal
					shipmentId={shipment.id}
					modalHeading={t('page-overview.deleteShipment')}
					page={page}
					onClose={() => {
						setDeleteShipmentModal(false);
					}}
				/>
			)}
			{hideShipmentModal && (
				<HideShipmentModal
					shipmentId={shipment.id}
					modalHeading={t('page-overview.hideShipmentModal')}
					page={page}
					onClose={() => {
						setHideShipmentModal(false);
					}}
					direction={shipment.direction}
					includeHidden={includeHidden}
				/>
			)}
		</StyledShipmentCard.ShipmentCard>
	);
};


const Grid = styled.section`
	display: grid;
	grid-template-columns: 1fr 3rem;
`;

const SubGrid = styled.section`
	display: grid;
	grid-template-rows: repeat(2, 3rem);
	row-gap: 4px;
`;

const StyledBin = styled(Bin)`
	color: ${({ theme }) => theme.colors.secondary};
	width: 2.5rem;
	height: 2.5rem;
	${media.tablet`
		width: 2rem;
		height: 2rem;
	`};
`;

const IconWrapper = styled.section`
	display: flex;
	align-items: center;
	justify-content: center;
	position: relative;
	width: 3rem;
	height: 3rem;
	line-height: 0;
	cursor: pointer;
	text-align: center;
 
	&::before {
    content: '';
    position: absolute;
    top: -6px;
    left: -6px;
    right: -6px;
    bottom: -6px;
    border-radius: 50%;
    opacity: 0;
    background-color: rgb(0,0,0,0.15);
    transition: opacity 150ms ease-in-out;
    z-index: 1;
  }
  
  &:hover::before {
    opacity: 1;
  }
`;

export default ShipmentCard;
